import type { ReactNode } from "react";

import { styled } from "@linaria/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import FlexWrapper, { AlignItems, FlexDirection } from "../containers/FlexWrapper";
import Text, { TextSize, TextVariant, TextWeight } from "../text/Text";

export enum MetricItemVariant {
  BASE = "BASE",
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
}

interface MetricItemProps {
  label: string;
  value: ReactNode;
  variant?: MetricItemVariant;
  noBorder?: boolean;
}

const MetricItemWrapper = withTheme(styled.div<
  PropsWithTheme<{ $variant: MetricItemVariant; $noBorder?: boolean }>
>`
  display: flex;
  flex-shrink: 0;
  flex-basis: auto;
  min-width: 140px;
  background-color: ${({ theme, $variant }) =>
    match($variant)
      .with(MetricItemVariant.BASE, () => theme.color.background.base)
      .with(MetricItemVariant.PRIMARY, () => theme.color.background.primary)
      .with(MetricItemVariant.SECONDARY, () => theme.color.background.secondary)
      .with(MetricItemVariant.TERTIARY, () => theme.color.background.tertiary)
      .exhaustive()};
  padding: 12px 16px;
  border: 0.5px solid
    ${({ theme, $noBorder }) => ($noBorder ? "transparent" : theme.color.border.primary)};
  border-radius: 5px;
`);

const MetricItem = ({
  label,
  value,
  variant = MetricItemVariant.PRIMARY,
  noBorder,
}: MetricItemProps) => {
  return (
    <MetricItemWrapper $variant={variant} $noBorder={noBorder}>
      <FlexWrapper direction={FlexDirection.COLUMN} gap={8} minWidth={0}>
        <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY} isEllipsis>
          {label}
        </Text>
        <FlexWrapper alignItems={AlignItems.CENTER} gap={6}>
          {typeof value === "string" ? (
            <Text size={TextSize.BODY_LG} weight={TextWeight.MEDIUM}>
              {value}
            </Text>
          ) : (
            value
          )}
        </FlexWrapper>
      </FlexWrapper>
    </MetricItemWrapper>
  );
};

export default MetricItem;
